import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Short label for where the error happened, e.g. "window" or "app". */
  scope?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Catches render-time crashes so one broken view can't blank the whole app.
// Shows the real error message + stack so users can report something actionable.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error(`[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`, error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="h-full w-full overflow-auto bg-red-50 p-6">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <span className="material-symbols-outlined">error</span>
            <h2 className="text-base font-bold">Something went wrong{this.props.scope ? ` in this ${this.props.scope}` : ''}</h2>
          </div>
          <p className="text-sm text-red-800 font-mono break-all mb-3">
            {error.message || String(error) || 'Unknown error'}
          </p>
          {error.stack && (
            <pre className="text-[11px] leading-snug text-red-900/70 bg-red-100/60 rounded-lg p-3 overflow-x-auto max-h-48 mb-4 whitespace-pre-wrap break-all">
              {error.stack}
            </pre>
          )}
          <button
            onClick={() => this.setState({ error: null })}
            className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
